import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAuthStore } from '@stores/AuthStore.js'

/**
 * The client's in-app notification feed + unread badge (SUB-MVP-45). Reads the
 * finder feed and clears the badge. Not persisted — re-fetched on each visit.
 * Mirrors ChatStore's Bearer auth + error handling.
 */
export const useNotificationsStore = defineStore('notificationsStore', () => {
  const authStore = useAuthStore()

  const items = ref([])
  const unread = ref(0)
  const loading = ref(false)
  const loadError = ref(false)
  const marking = ref(false)

  const headers = () => ({ Authorization: `Bearer ${authStore.token}` })

  const handleAuthError = (error) => {
    if ([401, 403].includes(error?.response?.status)) {
      authStore.clearSession()
      if (!import.meta.env.SSR) window.location.href = '/'
      return true
    }
    return false
  }

  const load = async () => {
    if (!authStore.token) return
    loading.value = true
    loadError.value = false
    try {
      const { data } = await axios.get('/api/client/notifications', { headers: headers() })
      items.value = data.data ?? []
      unread.value = data.unread ?? 0
    } catch (error) {
      if (!handleAuthError(error)) loadError.value = true
    } finally {
      loading.value = false
    }
  }

  const markAllRead = async () => {
    if (unread.value === 0) return
    marking.value = true
    try {
      await axios.post('/api/client/notifications/read', {}, { headers: headers() })
      unread.value = 0
      items.value = items.value.map((item) => ({ ...item, read: true }))
    } catch (error) {
      handleAuthError(error)
    } finally {
      marking.value = false
    }
  }

  const reset = () => {
    items.value = []
    unread.value = 0
    loadError.value = false
  }

  return { items, unread, loading, loadError, marking, load, markAllRead, reset }
})
